import Spline from "@splinetool/react-spline";
import clsx from "clsx";
import useUtils from "@/hooks/useUtils";
import { DEFAULT_BG_COLOR, DESKTOP } from "@/utils/appConstants";
import { BackgroundPropTypes } from "./propTypes";
import "./styles.scss";

const IFRAME_REGEX = /src="(.+?)"/;

export const hexToRgba = (hex = "#000000", alpha = 0.4) => {
  let h = String(hex).replace("#", "").trim();

  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const r = parseInt(h.slice(0, 2), 16) || 0;
  const g = parseInt(h.slice(2, 4), 16) || 0;
  const b = parseInt(h.slice(4, 6), 16) || 0;
  const a = Math.max(0, Math.min(1, Number(alpha)));

  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const Background = (props) => {
  const {
    asBackground = true,
    background_3d_model_file: background3dModelFile,
    background_3d_model: background3dModel,
    background_color: backgroundColor = DEFAULT_BG_COLOR,
    background_embed: backgroundEmbed,
    background_gradient_group: backgroundGradientGroup = {},
    background_image_min_height: backgroundImageMinHeight,
    background_image_position: backgroundImagePosition = "center",
    background_image: backgroundImage,
    background_solid_color_group: backgroundSolidColorGroup = {},
    background_type: backgroundType,
    background_video: backgroundVideo,
  } = props;

  const {
    background_solid_color: backgroundSolidColor,
    background_solid_color_opacity: backgroundSolidColorOpacity,
  } = backgroundSolidColorGroup;

  const {
    background_gradient_direction: backgroundGradientDirection,
    background_gradient_end_color: backgroundGradientEndColor,
    background_gradient_end_opacity: backgroundGradientEndOpacity,
    background_gradient_start_color: backgroundGradientStartColor,
    background_gradient_start_opacity: backgroundGradientStartOpacity,
  } = backgroundGradientGroup;

  const { device } = useUtils();
  const desktop = device === DESKTOP;

  const matches = backgroundEmbed?.match(IFRAME_REGEX);

  const iframeSrc = matches?.[1]
    ? `${matches?.[1]}&autoplay=1&mute=1&loop=1&controls=0&showinfo=0&modestbranding=1&rel=0`
    : null;

  const backgroundImageStyle = {
    ...(backgroundImageMinHeight && !desktop && { minHeight: `${backgroundImageMinHeight}px` }),
  };

  switch (backgroundType) {
    case "image":
      return (
        <div
          className={clsx("background-component-image-container", {
            "as-background": asBackground,
          })}
          style={backgroundImageStyle}
        >
          {backgroundImage && (
            <img
              alt={backgroundImage.alt}
              className={clsx(`background-component`, {
                "section-component": !asBackground,
                "background-image": !!backgroundImage,
                [`image-position-${backgroundImagePosition}`]: !!backgroundImagePosition,
              })}
              src={backgroundImage.url}
              loading="lazy"
            />
          )}
        </div>
      );

    case "video":
      return (
        <>
          {backgroundVideo && (
            <video
              autoPlay={asBackground}
              className={clsx("background-component overflow-hidden", {
                "section-component": !asBackground,
              })}
              controls={!asBackground}
              loop={asBackground}
              muted={asBackground}
              playsInline
              src={backgroundVideo.url}
              loading="lazy"
            />
          )}
        </>
      );

    case "embed":
      return (
        <>
          {backgroundEmbed && iframeSrc && (
            <div
              className={clsx("background-component overflow-hidden", {
                "section-component": !asBackground,
              })}
            >
              <div className="relative overflow-hidden w-full h-0 pb-[56.25%]">
                <iframe
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full border-none"
                  src={iframeSrc}
                  title="Video"
                  loading="lazy"
                />
              </div>
            </div>
          )}
        </>
      );

    case "color":
      return (
        <>
          {backgroundColor && (
            <div
              className={clsx("background-component", {
                "section-component": !asBackground,
              })}
              style={{ backgroundColor: backgroundColor }}
            />
          )}
        </>
      );

    case "solid":
      return (
        <>
          {backgroundSolidColor && (
            <div
              className={clsx("background-component", {
                "section-component": !asBackground,
              })}
              style={{
                backgroundColor: hexToRgba(backgroundSolidColor, backgroundSolidColorOpacity),
              }}
            />
          )}
        </>
      );

    case "gradient":
      return (
        <>
          {backgroundGradientStartColor && backgroundGradientEndColor && (
            <div
              className={clsx("background-component", {
                "section-component": !asBackground,
              })}
              style={{
                background: `linear-gradient(
                ${backgroundGradientDirection},
                ${hexToRgba(backgroundGradientStartColor, backgroundGradientStartOpacity)} 0%,
                ${hexToRgba(backgroundGradientEndColor, backgroundGradientEndOpacity)} 100%
                )`,
              }}
            />
          )}
        </>
      );

    case "3d-model":
      return (
        <>
          {background3dModel && (
            <Spline
              className={clsx("background-component", {
                "section-component": !asBackground,
              })}
              scene={background3dModel}
              loading="lazy"
            />
          )}
        </>
      );

    case "3d-model-file":
      return (
        <>
          {background3dModelFile && (
            <Spline
              className={clsx("background-component", {
                "section-component": !asBackground,
              })}
              scene={background3dModelFile.url}
              loading="lazy"
            />
          )}
        </>
      );

    default:
      return null;
  }
};

Background.propTypes = BackgroundPropTypes;

export default Background;
